<script setup>
import { ref, onBeforeMount, onMounted, onUnmounted, reactive, watch, computed } from 'vue';
import { callAPI, DRUPAL_API_ROUTE } from "@/service/DrupalService";
import Skeleton from 'primevue/skeleton';
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { useLayout } from '@/layout/composables/layout';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

const { toggleDarkMode, isDarkTheme, toggleDevMode, isDevMode } = useLayout();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  shortlink: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:visible']);
const localVisible = ref(props.visible);

const confirm = useConfirm();
const toast = useToast();


const homepage = import.meta.env.VITE_DRUPAL_URL;
const user = ref(JSON.parse(localStorage.getItem('user')));
const userProfileDialog = ref(false);
const userProfileLoading = ref(false);
const userProfileUpdateLoading = ref(false);
const websiteError = ref('');
const mailError = ref('');
const userJournalDialog = ref(false);
const userAPIKeyDialog = ref(false);
const userAPIKeyLoading = ref(false);
const userJournal = ref([]);
const lazyLoadingJournal = ref(false);
const userSourceDialog = ref(false);
const userSourceLoading = ref(false);
const userInvoicesDialog = ref(false);
const userInvoicesLoading = ref(false);

const userSubscriptionDialog = ref(false);
const userSourceUpdateLoading = ref(false);
const vatValidationLoading = ref(false);
const userAPIKeyCreateLoading = ref(false);
const userDeleteAccountDialog = ref(false);
const userDeleteAccountLoading = ref(false);

const initialState = {
  countriesList: [],
  field_account_firstname: '',
  field_account_lastname: '',
  field_account_company: '',
  field_account_website: '',
  mail: '',
  preferred_langcode: '',
  timezone: '',
  passwordCurrent: '',
  passwordNew: '',
  passwordConfirm: '',
  userStripeCustomer: {
    address: {
      line1: null,
      postal_code: null,
      city: null,
      country: null
    },
    default_source: null,
    tax_id: null,
    tax_ids: null,
    subscriptions: null
  },
  passwordValidate: '',
  userProfileChangePasswordRequired: false,
  userSourceCardUpdate: homepage + '/user/stripe/edit-card',
  userAPIKey: [],
  userDeleteAccount: {
    confirm: false,
    deleteAccountReason: false,
    deleteAccountComment: '',
  },
  userInvoices: []
};

const formData = reactive({ ...initialState });

const pricingURLData = import.meta.env.VITE_PRICING_URL.split(',');

const pricingURL = computed(() => {
  switch (locale.value) {
    case 'fr': return pricingURLData[0];
    case 'en': return pricingURLData[1];
    case 'de': return pricingURLData[2];
    case 'es': return pricingURLData[3];
    case 'it': return pricingURLData[4];
    case 'pt': return pricingURLData[5];
    default: return 'https://lc.cx';
  }
});

const items = computed(() => [
  {
    items: [
      {
        label: t('account.menu.informations.title'),
        icon: 'pi pi-id-card',
        command: () => {
          userProfileDialog.value = true;
        }
      },
      {
        label: t('account.menu.plan.title'),
        icon: 'pi pi-ticket',
        command: () => {
          userSubscriptionDialog.value = true;
        }
      },
      {
        label: t('account.menu.billing.title'),
        icon: 'pi pi-credit-card',
        command: () => {
          userSourceDialog.value = true;
        }
      },
      {
        label: t('account.menu.invoices.title'),
        icon: 'pi pi-receipt',
        command: () => {
          userInvoicesDialog.value = true;
        }
      },
      {
        label: t('account.menu.password.title'),
        icon: 'pi pi-lock',
        url: homepage + '/user/' + user.value.uid + '/edit?password=1'
      },
      {
        label: t('account.menu.2fa.title'),
        icon: 'pi pi-shield',
        url: homepage + '/user/' + user.value.uid + '/security/tfa'
      },
      {
        label: t('account.menu.apikey.title'),
        icon: 'pi pi-key',
        command: () => {
          userAPIKeyDialog.value = true;
        }
      },
      {
        label: t('account.menu.journal.title'),
        icon: 'pi pi-clipboard',
        command: () => {
          userJournalDialog.value = true;
        }
      }
    ]
  }
]);

const logoutConfirm = () => {
  confirm.require({
    message: t('account.logout.message'),
    header: t('account.logout.header'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: t('common.labels.cancel'),
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'OK'
    },
    accept: async () => {
      localStorage.clear();
      window.location.href = homepage + '/' + user.value.language + '/user/logout';
    },
  });
};

const userDeleteAccountConfirm = async () => {
  if (formData.userDeleteAccount.confirm) {
    userDeleteAccountLoading.value = true;

    var data = {
      'field_account_leaving': [{ 'value': formData.userDeleteAccount.deleteAccountReason }],
      'field_account_leaving_comment': [{ 'value': formData.userDeleteAccount.deleteAccountComment }],
    }

    await callAPI(DRUPAL_API_ROUTE.USER_UPDATE, { uid: user.value.uid }, data);
    await callAPI(DRUPAL_API_ROUTE.USER_CANCEL, { uid: user.value.uid }, { 'status': [{ 'value': 0 }] });

    localStorage.clear();
    userDeleteAccountLoading.value = false;
    window.location.href = homepage + '/' + user.value.language;
  }
}


const getUser = async () => {
  userProfileLoading.value = true;

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.USER_DATA, { uid: user.value.uid });

    formData.field_account_firstname = response.data.field_account_firstname?.[0]?.value;
    formData.field_account_lastname = response.data.field_account_lastname?.[0]?.value;
    formData.field_account_company = response.data.field_account_company?.[0]?.value;
    formData.field_account_website = response.data.field_account_website?.[0]?.uri;
    formData.mail = response.data.mail?.[0]?.value;

    await fetchLanguages();
    formData.language = response.data.preferred_langcode?.[0]?.value;

    await fetchTimezones();
    formData.timezone = response.data.timezone?.[0]?.value;

  } catch (error) {
    console.log(error)
  } finally {
    userProfileLoading.value = false;
  }
}

const fetchLanguages = async () => {
  var response = await callAPI(DRUPAL_API_ROUTE.MISCELLANEOUS_LANGUAGES);
  formData.countriesList = response.data;

};

const fetchTimezones = async () => {
  var response = await callAPI(DRUPAL_API_ROUTE.MISCELLANEOUS_TIMEZONES);
  formData.timezonesList = response.data;
};

const userProfileUpdate = async () => {
  websiteError.value = '';
  mailError.value = '';
  userProfileUpdateLoading.value = true;

  var data = {
    'mail': [{ 'value': formData.mail }],
    'field_account_firstname': [{ 'value': formData.field_account_firstname }],
    'field_account_lastname': [{ 'value': formData.field_account_lastname }],
    'field_account_company': [{ 'value': formData.field_account_company }],
    'field_account_website': [{ 'uri': formData.field_account_website }],
    'preferred_langcode': [{ 'value': formData.language }],
    'timezone': [{ 'value': formData.timezone }],
  }

  if (formData.passwordValidate) data.pass = [{ 'existing': formData.passwordValidate }];

  const result = await callAPI(DRUPAL_API_ROUTE.USER_UPDATE, { uid: user.value.uid }, data);
  userProfileUpdateLoading.value = false;

  if (result?.status >= 400) {
    let handled = false;
    if (result.message?.includes('field_account_website')) {
      websiteError.value = t('account.informations.website_error');
      handled = true;
    }
    if (result.message?.includes('mail.0.value') || result.message?.includes('mail.value')) {
      mailError.value = t('account.informations.mail_error');
      handled = true;
    }
    if (!handled) {
      toast.add({ severity: 'error', summary: t('account.informations.title'), detail: result.message, life: 5000 });
    }
    return;
  }

  locale.value = formData.language;
  userProfileDialog.value = false;
  toast.add({ severity: 'success', summary: t('account.informations.title'), detail: t('account.informations.success'), life: 2000 });
}

const getUserJournal = async () => {
  userJournalDialog.value = true;

  var response = await callAPI(DRUPAL_API_ROUTE.USER_JOURNAL, { offset: 0, limit: 50 });
  userJournal.value = response.data.journal;
}

const loadJournalLazy = async (event) => {
  !lazyLoadingJournal.value && (lazyLoadingJournal.value = true);


  const { first, last } = event;
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.USER_JOURNAL, { offset: first, limit: last });

    let _userJournal = [...userJournal.value];
    Array.prototype.splice.apply(_userJournal, [
      ...[first, last],
      ...response.data.journal,
    ]);

    userJournal.value = _userJournal;

  } catch (error) {
    console.log(error)
  } finally {
    lazyLoadingJournal.value = false;
  }
}

const getUserInvoices = async () => {
  userInvoicesLoading.value = true;
  var response = await callAPI(DRUPAL_API_ROUTE.STRIPE_INVOICES);
  formData.userInvoices = response.data;
  userInvoicesLoading.value = false;
}

const invoiceDownload = (fileUrl) => {
  var link = document.createElement('a');
  link.href = fileUrl;
  link.click();
}

const getUserCustomer = async () => {
  userSourceLoading.value = true;

  var response = await callAPI(DRUPAL_API_ROUTE.STRIPE_CUSTOMER);

  if (response.status == 200) {
    formData.userStripeCustomer = response.data;
    if (formData.userStripeCustomer.invoice_settings.default_payment_method?.card) {
      formData.userStripeCustomer.default_source = formData.userStripeCustomer.invoice_settings.default_payment_method.card.brand + ' ' +
        formData.userStripeCustomer.invoice_settings.default_payment_method.card.last4 + ' - Exp : ' +
        formData.userStripeCustomer.invoice_settings.default_payment_method.card.exp_month + '/' +
        formData.userStripeCustomer.invoice_settings.default_payment_method.card.exp_year;

      if (formData.userStripeCustomer.tax_ids?.data[0].value) {
        formData.userStripeCustomer.tax_id = formData.userStripeCustomer.tax_ids?.data[0].value;
      }

    } else if (formData.userStripeCustomer.default_source) {
      formData.userStripeCustomer.default_source = formData.userStripeCustomer?.default_source.brand + ' ' +
        formData.userStripeCustomer?.default_source.last4 + ' - Exp : ' +
        formData.userStripeCustomer?.default_source.exp_month + '/' +
        formData.userStripeCustomer?.default_source.exp_year;

      if (formData.userStripeCustomer.tax_ids?.data[0].value) {
        formData.userStripeCustomer.tax_id = formData.userStripeCustomer.tax_ids?.data[0].value;
      }
    } else {
      formData.userStripeCustomer.result = false;
    }

    if (formData.userStripeCustomer.subscriptions?.data.length > 0) {
      formData.userStripeCustomer.result = true;
      formData.userStripeCustomer.subscriptions.data[0].plan.fullname = formData.userStripeCustomer?.subscriptions.data[0].plan.nickname + '  ' +
        formData.userStripeCustomer?.subscriptions.data[0].plan.amount / 100 + ' € HT / ' + formData.userStripeCustomer?.subscriptions.data[0].plan.interval;
    }
  }

  await fetchCountries();
  userSourceLoading.value = false;
}

const userSourceUpdate = async () => {
  userSourceUpdateLoading.value = true;

  var data = {
    'name': formData.userStripeCustomer.name,
    'address': {
      'line1': formData.userStripeCustomer.address.line1,
      'postal_code': formData.userStripeCustomer.address.postal_code,
      'city': formData.userStripeCustomer.address.city,
      'country': formData.userStripeCustomer.address.country,
    },
    'preferred_locales': user.value.language,
    'eu_vat': formData.userStripeCustomer.tax_id
  }

  await callAPI(DRUPAL_API_ROUTE.STRIPE_CUSTOMER_UPSERT, {}, data);

  userSourceDialog.value = false;
  userSourceUpdateLoading.value = false;

  toast.add({ severity: 'success', summary: t('account.billing.title'), detail: t('account.billing.success'), life: 2000 });
}

const userSourceDelete = async () => {
  confirm.require({
    message: t('account.billing.source.delete.message'),
    header: t('common.labels.delete'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: t('common.labels.cancel'),
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'OK'
    },
    accept: async () => {
      await callAPI(DRUPAL_API_ROUTE.STRIPE_CUSTOMER_CARD_DELETE);
      await getUserCustomer();
      toast.add({ severity: 'success', summary: t('common.labels.delete'), detail: t('account.billing.source.delete.success'), life: 2000 });
    },
  });
}

const userCancelSubscriptionConfirm = async () => {
  confirm.require({
    message: t('account.plan.cancel.confirm'),
    header: t('common.labels.delete'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: t('common.labels.cancel'),
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'OK'
    },
    accept: async () => {
      await callAPI(DRUPAL_API_ROUTE.STRIPE_CANCEL);
      await getUserCustomer();
      toast.add({ severity: 'success', summary: t('common.labels.delete'), detail: t('account.plan.cancel.success'), life: 2000 });
    },
  });
}

const getUserAPIKey = async () => {
  userAPIKeyLoading.value = true;
  var request = await callAPI(DRUPAL_API_ROUTE.USER_APIKEY);
  if (request.status == 200) {
    formData.userAPIKey = request.data;
  }

  userAPIKeyLoading.value = false;
}

const userAPIKeyCreate = async () => {
  userAPIKeyCreateLoading.value = true;

  await callAPI(DRUPAL_API_ROUTE.USER_APIKEY_GENERATE);
  await getUserAPIKey();
  toast.add({ severity: 'success', summary: t('account.apikey.title'), detail: t('account.apikey.creation.success'), life: 2000 });
  userAPIKeyCreateLoading.value = false;
}

const userAPIKeyDelete = async (uuid) => {
  confirm.require({
    message: t('account.apikey.delete.confirm'),
    header: t('common.labels.delete'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: t('common.labels.cancel'),
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'OK'
    },
    accept: async () => {
      await callAPI(DRUPAL_API_ROUTE.USER_APIKEY_DELETE, { uuid: uuid });
      await getUserAPIKey();
      toast.add({ severity: 'success', summary: t('common.labels.delete'), detail: t('account.apikey.delete.success'), life: 2000 });
    },
  });
}

const fetchCountries = async () => {
  var response = await callAPI(DRUPAL_API_ROUTE.MISCELLANEOUS_COUNTRIES, { language: user.value.language });
  formData.countriesList = response.data;
};

const vatValidation = async () => {
  if (formData.userStripeCustomer.tax_id) {
    vatValidationLoading.value = true;
    var response = await callAPI(DRUPAL_API_ROUTE.MISCELLANEOUS_VATVALIDATION, {}, { 'eu_vat': formData.userStripeCustomer.tax_id });
    console.log(response.data);
    vatValidationLoading.value = false;
  }
};

function getInitials(text) {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  const initials = words.map(word => word.charAt(0).toUpperCase());

  return initials.join('').slice(0, 2);
}

const copyToClipboard = async (textToCopy) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    textArea.style.position = "absolute";
    textArea.style.left = "-999999px";

    document.body.prepend(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (error) {
      console.error(error);
    } finally {
      textArea.remove();
    }
  }
  toast.add({ severity: 'info', summary: t('common.labels.copy'), detail: t('account.apikey.copy'), life: 5000 });
};

function resetForm() {
  websiteError.value = '';
  mailError.value = '';
  Object.assign(formData, initialState);
  formData.userStripeCustomer.address.line1 = null;
  formData.userStripeCustomer.address.postal_code = null;
  formData.userStripeCustomer.address.city = null;
  formData.userStripeCustomer.address.country = null;
  formData.userStripeCustomer.default_source = null;
  formData.userStripeCustomer.tax_id = null;
  formData.userStripeCustomer.tax_ids = null;
  formData.userStripeCustomer.subscriptions = null;

  formData.userDeleteAccount.confirm = false;
  formData.userDeleteAccount.deleteAccountReason = false;
  formData.userDeleteAccount.deleteAccountComment = '';
}

async function handleShow() {
  return;
}

function handleHide() {
  resetForm();
  const topbarElement = document.querySelector('.layout-topbar');
  if (topbarElement) {
    topbarElement.style.zIndex = '997';
  }
  emit('update:visible', false);
}

watch(
  () => props.visible,
  (newValue) => {
    localVisible.value = newValue;
  }
)
</script>


<template>

  <Dialog v-model:visible="userSubscriptionDialog" modal :header="t('account.plan.header')" @show="getUserCustomer"
    @hide="resetForm" class="w-96">
    <div class="flex flex-col gap-4 mb-8">
      <div class="flex">
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <Tag v-if="!userSourceLoading && formData.userStripeCustomer && formData.userStripeCustomer.result"
          icon="pi pi-ticket" :value="formData.userStripeCustomer.subscriptions.data[0].plan.fullname"></Tag>
        <Tag v-if="!userSourceLoading && !formData.userStripeCustomer.result" icon="pi pi-ticket"
          value="Open (gratuit)"></Tag>
        <div v-if="!userSourceLoading && formData.userStripeCustomer" class="ml-auto space-x-2">
          <Button as="a" :href="pricingURL" icon="pi pi-pencil" v-tooltip.left="t('common.labels.update')"
            severity="primary" />
          <Button v-if="formData.userStripeCustomer.result" @click="userCancelSubscriptionConfirm" icon="pi pi-trash"
            v-tooltip.left="t('common.labels.delete')" severity="danger" />
        </div>
      </div>
      <div v-if="formData.userStripeCustomer.result" class="flex flex-col gap-2">
        <label for="created" class="font-semibold">{{ t('account.plan.dates.start') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userSourceLoading && formData.userStripeCustomer"
          v-model="formData.userStripeCustomer.subscriptions.data[0].plan.created" id="created" disabled
          class="flex-auto" />
      </div>
      <div v-if="formData.userStripeCustomer.result" class="flex flex-col gap-2">
        <label for="update" class="font-semibold">{{ t('account.plan.dates.renew') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userSourceLoading && formData.userStripeCustomer"
          v-model="formData.userStripeCustomer.subscriptions.data[0].current_period_end" id="update" disabled
          class="flex-auto" />
      </div>
      <div v-if="formData.userStripeCustomer.result">
        <Message v-if="formData.userStripeCustomer.subscriptions.data[0].cancel_at_period_end">
          {{ t('account.plan.cancel.success') }}</Message>
      </div>
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.close')" severity="secondary"
        @click="userSubscriptionDialog = false"></Button>
    </div>
  </Dialog>

  <Dialog v-model:visible="userDeleteAccountDialog" modal :header="t('account.delete.title')" @hide="resetForm"
    :style="{ width: '30rem' }">
    <div class="flex flex-col mb-8 gap-4">
      <span class="text-surface-500 dark:text-surface-400 block mt-4 mb-4">{{ t('account.delete.subtitle') }}</span>
      <div class="flex items-center space-x-4">
        <Checkbox v-model="formData.userDeleteAccount.confirm" binary />
        <label for="deleteAccountNotice">{{ t('account.delete.confirm.label') }}</label>
      </div>
      <div v-if="formData.userDeleteAccount.confirm" class="flex flex-col flex-wrap gap-4">
        <span class="text-surface-500 dark:text-surface-400 block mt-4 mb-4">
          {{ t('account.delete.confirm.help') }}
        </span>
        <div class="flex items-center gap-2">
          <RadioButton v-model="formData.userDeleteAccount.deleteAccountReason" value="0" inputId="deleteAccountReason1"
            name="deleteAccountReason1" />
          <label for="deleteAccountReason1">{{ t('account.delete.confirm.reason1') }}</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton v-model="formData.userDeleteAccount.deleteAccountReason" value="1" inputId="deleteAccountReason2"
            name="deleteAccountReason2" />
          <label for="deleteAccountReason2">{{ t('account.delete.confirm.reason2') }}</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton v-model="formData.userDeleteAccount.deleteAccountReason" value="2" inputId="deleteAccountReason3"
            name="deleteAccountReason3" />
          <label for="deleteAccountReason3">{{ t('account.delete.confirm.reason3') }}</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton v-model="formData.userDeleteAccount.deleteAccountReason" value="3" inputId="deleteAccountReason4"
            name="deleteAccountReason4" />
          <label for="deleteAccountReason4">{{ t('account.delete.confirm.reason4') }}</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton v-model="formData.userDeleteAccount.deleteAccountReason" value="4" inputId="deleteAccountReason5"
            name="deleteAccountReason5" />
          <label for="deleteAccountReason5">{{ t('account.delete.confirm.reason5') }}</label>
        </div>
        <div class="flex flex-col gap-2">
          <label for="deleteAccountComment">{{ t('account.delete.confirm.comment') }}</label>
          <Textarea v-model="formData.userDeleteAccount.deleteAccountComment" id="deleteAccountComment"
            class="flex-auto" />
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.close')" severity="secondary"
        @click="userDeleteAccountDialog = false"></Button>
      <Button type="button" :label="t('common.labels.delete')" severity="danger" @click="userDeleteAccountConfirm"
        :loading="userDeleteAccountLoading" :disabled="!formData.userDeleteAccount.confirm"></Button>
    </div>
  </Dialog>

  <Dialog v-model:visible="userAPIKeyDialog" modal :header="t('account.apikey.title')" @show="getUserAPIKey"
    @hide="resetForm" :style="{ width: '30rem' }">
    <Skeleton v-if="userAPIKeyLoading" height="3rem" class="mb-2" />
    <div v-if="!userAPIKeyLoading && formData.userAPIKey.length > 0" class="flex gap-4 mb-8">
      <InputGroup>
        <InputText :value=formData.userAPIKey[0].apikey readonly>
        </InputText>
        <Button @click="copyToClipboard(formData.userAPIKey[0].apikey)" v-tooltip.bottom="t('common.labels.copy')"
          icon="pi pi-copy" size="small" />
      </InputGroup>
      <div class="ml-auto space-x-2">
        <Button @click="userAPIKeyDelete(formData.userAPIKey[0].id)" icon="pi pi-trash"
          v-tooltip.left="t('common.labels.revoke')" severity="danger" />
      </div>
    </div>
    <div v-else-if="!userAPIKeyLoading" class="flex justify-center mb-8">
      <Button @click="userAPIKeyCreate" icon="pi pi-cog" :label="t('account.apikey.create')"
        :loading="userAPIKeyCreateLoading" severity="primary" />
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.close')" severity="secondary"
        @click="userAPIKeyDialog = false"></Button>
    </div>
  </Dialog>

  <Dialog v-model:visible="userSourceDialog" modal :header="t('account.billing.source.title')" @show="getUserCustomer"
    @hide="resetForm" class="w-96">
    <div class="flex flex-col gap-4 mb-8">
      <span class="text-surface-500 dark:text-surface-400 block mt-4 mb-2">{{ t('account.billing.source.subtitle')
        }}</span>
      <div class="flex">
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <Tag v-if="!userSourceLoading && formData.userStripeCustomer && formData.userStripeCustomer.default_source"
          icon="pi pi-credit-card" :value="formData.userStripeCustomer?.default_source"></Tag>
        <Tag v-else-if="!userSourceLoading" icon="pi pi-credit-card" value="Aucun">
        </Tag>

        <div v-if="!userSourceLoading && formData.userStripeCustomer" class="ml-auto space-x-2">
          <Button v-if="!formData.userStripeCustomer.default_source" as="a" :v-tooltip.left="t('common.labels.add')"
            icon="pi pi-plus" :href="formData.userSourceCardUpdate" />
          <Button v-if="formData.userStripeCustomer.default_source" :v-tooltip.left="t('common.labels.delete')"
            icon="pi pi-trash" severity="danger" @click="userSourceDelete" />
        </div>
      </div>
      <span class="text-surface-500 dark:text-surface-400 block mt-4 mb-1">{{ t('account.billing.source.contact')
      }}</span>
      <div class="flex flex-col gap-2">
        <label for="company" class="font-semibold">{{ t('account.billing.source.name') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userSourceLoading && formData.userStripeCustomer" v-model="formData.userStripeCustomer.name"
          id="fullname" class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="address" class="font-semibold">{{ t('account.billing.source.address') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userSourceLoading && formData.userStripeCustomer"
          v-model="formData.userStripeCustomer.address.line1" id="adress" class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="zip" class="font-semibold">{{ t('account.billing.source.pc') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userSourceLoading && formData.userStripeCustomer"
          v-model="formData.userStripeCustomer.address.postal_code" id="zip" class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="city" class="font-semibold">{{ t('account.billing.source.city') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userSourceLoading && formData.userStripeCustomer"
          v-model="formData.userStripeCustomer.address.city" id="city" class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="country" class="font-semibold">{{ t('account.billing.source.country') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <Select v-if="!userSourceLoading && formData.userStripeCustomer"
          v-model="formData.userStripeCustomer.address.country" :options="formData.countriesList" optionValue="code"
          optionLabel="name" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="eu_vat" class="font-semibold">{{ t('account.billing.source.vat') }}</label>
        <Skeleton v-if="userSourceLoading" height="3rem" class="mb-2" />
        <InputGroup v-else>
          <InputText v-if="!userSourceLoading && formData.userStripeCustomer"
            v-model="formData.userStripeCustomer.tax_id" id="eu_vat"
            :disabled="vatValidationLoading || !!formData.userStripeCustomer.name" class="flex-auto w-full" />
          <Button v-if="!formData.userStripeCustomer.name" @click="vatValidation" :loading="vatValidationLoading"
            icon="pi pi-search" />
        </InputGroup>
      </div>
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.close')" severity="secondary"
        @click="userSourceDialog = false"></Button>
      <Button type="button" :label="t('common.labels.save')" @click="userSourceUpdate"
        :loading="userSourceUpdateLoading"></Button>
    </div>
  </Dialog>

  <Dialog v-model:visible="userProfileDialog" modal :header="t('account.informations.title')" @show="getUser"
    @hide="resetForm" class="w-96">
    <div class="flex flex-col gap-4 mb-8">
      <div class="flex flex-col gap-2">
        <label for="fistname" class="font-semibold">{{ t('account.informations.firstname') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userProfileLoading" v-model="formData.field_account_firstname" id="firstname"
          class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="lastname" class="font-semibold">{{ t('account.informations.lastname') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userProfileLoading" v-model="formData.field_account_lastname" id="lastname"
          class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="company" class="font-semibold">{{ t('account.informations.company') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userProfileLoading" v-model="formData.field_account_company" id="company"
          class="flex-auto" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="website" class="font-semibold">{{ t('account.informations.website') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userProfileLoading" v-model="formData.field_account_website" id="website"
          class="flex-auto" :invalid="!!websiteError" />
        <Message v-if="websiteError" size="small" severity="error" variant="simple">{{ websiteError }}</Message>
      </div>
      <div class="flex flex-col gap-2">
        <label for="mail" class="font-semibold">{{ t('account.informations.mail') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <InputText v-if="!userProfileLoading" @change="formData.userProfileChangePasswordRequired = true"
          v-model="formData.mail" id="mail" class="flex-auto" :invalid="!!mailError" />
        <Message v-if="mailError" size="small" severity="error" variant="simple">{{ mailError }}</Message>
      </div>
      <div class="flex flex-col gap-2">
        <label for="mail" class="font-semibold">{{ t('account.informations.language') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <Select v-if="!userProfileLoading" :options="formData.countriesList" v-model="formData.language"
          optionValue="code" optionLabel="name" required />
      </div>
      <div class="flex flex-col gap-2">
        <label for="mail" class="font-semibold">{{ t('account.informations.timezone') }}</label>
        <Skeleton v-if="userProfileLoading" height="3rem" class="mb-2" />
        <Select v-if="!userProfileLoading" :options="formData.timezonesList" v-model="formData.timezone"
          optionValue="code" optionLabel="name" required />
      </div>
      <Fieldset v-if="!userProfileLoading" class="text-center" legend="Danger zone">
        <Button type="button" :label="t('account.informations.delete')" severity="danger"
          @click="userDeleteAccountDialog = true"></Button>
      </Fieldset>
      <div v-if="formData.userProfileChangePasswordRequired" class="flex flex-col gap-2 mt-4">
        <span class="text-surface-500 dark:text-surface-400 block mb-4">{{ t('account.informations.confirm') }}</span>
        <label for="password" class="font-semibold">{{ t('account.informations.password') }}</label>
        <Password class="flex-auto" :feedback="false" v-model="formData.passwordValidate" id="confirm_password" />
      </div>
    </div>
    <div v-if="!userProfileLoading" class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.cancel')" severity="secondary"
        @click="userProfileDialog = false"></Button>
      <Button type="button" :label="t('common.labels.save')" @click="userProfileUpdate"
        :loading="userProfileUpdateLoading"></Button>
    </div>
  </Dialog>

  <Dialog v-model:visible="userJournalDialog" @show="getUserJournal" @hide="userJournalDialog = false" modal
    :header="t('account.journal.title')" :style="{ width: '60rem' }">
    <div class="flex flex-col gap-4">
      <DataTable :value="userJournal" scrollable scrollHeight="400px" tableStyle="min-width: 50rem"
        :virtualScrollerOptions="{
          lazy: true,
          onLazyLoad: loadJournalLazy,
          itemSize: 46,
          delay: 200,
          showLoader: true,
          loading: lazyLoadingJournal,
          numToleratedItems: 10
        }">
        <Column field="created" :header="t('account.journal.period')" style="width: 20%">
          <template #loading>
            <div class="flex items-center" :style="{ height: '17px', 'flex-grow': '1', overflow: 'hidden' }">
              <Skeleton width="60%" height="1rem" />
            </div>
          </template>
        </Column>
        <Column field="ip" header="IP" style="width: 20%">
          <template #loading>
            <div class="flex items-center" :style="{ height: '17px', 'flex-grow': '1', overflow: 'hidden' }">
              <Skeleton width="60%" height="1rem" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </Dialog>

  <Dialog v-model:visible="userInvoicesDialog" modal :header="t('account.invoices.title')" @show="getUserInvoices"
    @hide="userInvoicesDialog = false" :style="{ width: '60rem' }">
    <div class="flex items-center gap-4 mb-4">
      <DataTable :loading="userInvoicesLoading" :value="formData.userInvoices" tableStyle="min-width: 55rem">
        <Column field="period" :header="t('account.invoices.period')"></Column>
        <Column field="status" :header="t('account.invoices.status')">
          <template #body="slotProps">
            <i v-if="slotProps.data.status == 'paid'" class="pi pi-check"></i>
            <i v-else class="pi pi-times"></i>
          </template>
        </Column>
        <Column field="invoice" :header="t('account.invoices.invoice')">
          <template #body="slotProps">
            <Button size="small" icon="pi pi-download" @click="invoiceDownload(slotProps.data.invoice_pdf)"
              v-tooltip.bottom="t('common.labels.download')" />
          </template>
        </Column>
      </DataTable>
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.close')" severity="secondary"
        @click="userInvoicesDialog = false"></Button>
    </div>
  </Dialog>


  <Fluid>
    <Drawer :blockScroll="true" :dismissable="false" @hide="handleHide" @show="handleShow"
      v-model:visible="localVisible" position="right" :header="' '">
      <div class="flex flex-col gap-10">
        <div class="flex flex-col gap-4 items-center justify-center">
          <div>
            <Avatar shape="circle" size="xlarge" :label="getInitials(user.fullname)" />
          </div>
          <div>{{ user.firstname }} {{ user.lastname }}</div>
        </div>
        <Menu :pt="{ separator: { class: 'my-10' }, item: { class: 'my-1' } }" :style="'border:0'" :model="items">
          <template #item="{ item, props }">
            <a v-if="!item.url" v-ripple class="flex items-center py-3!" v-bind="props.action">
              <span :class="[item.icon, item.color]" />
              <span :class="item.color">{{ item.label }}</span>
            </a>
            <a v-else v-ripple :href="item.url" class="flex items-center py-3!" v-bind="props.action">
              <span :class="[item.icon, item.color]" />
              <span :class="item.color">{{ item.label }}</span>
            </a>
          </template>
        </Menu>
      </div>
      <template #footer>
        <div class="flex justify-start space-x-4 w-full">
          <div class="flex space-x-4">
            <Button v-tooltip.top="t('account.menu.dev.title')" @click="toggleDevMode" outlined>
              <template #icon>
                <span class="relative inline-block w-5 h-5">
                  <i class="pi pi-code text-base"></i>
                  <span v-if="!isDevMode"
                    class="absolute top-1/2 left-1/2 w-[150%] h-[2px] bg-black/50 rotate-45 -translate-x-1/2 -translate-y-1/2"></span>
                </span>
              </template>
            </Button>
            <Button v-tooltip.top="isDarkTheme ? t('account.menu.theme.light') : t('account.menu.theme.dark')"
              :icon="isDarkTheme ? 'pi pi-moon' : 'pi pi-sun'" @click="toggleDarkMode" outlined />
          </div>
          <Button v-tooltip.top="t('account.menu.logout.title')" class="ml-auto" severity="danger" icon="pi pi-sign-out"
            @click="logoutConfirm()" outlined />
        </div>
      </template>
    </Drawer>
  </Fluid>
</template>
